import { format } from "date-fns";

import {
  FieldType,
  formatValue,
  OptionType,
  PaymentResponse,
} from "@smart/bridge-types-basic";
import {
  entriesOf,
  escapeForCSV,
  sortField,
  specialChars,
} from "@smart/itops-utils-basic";
import { Gql } from "@smart/manage-gql-operations-dom";

// This generates a CSV string from submissions using the same form
export const generateCSVData = ({
  submissions,
  formTitle,
  sections,
  fields,
}: {
  submissions: Gql.SubmissionFieldsFragment[] | undefined;
  formTitle: string;
  sections: Gql.SectionFieldsFragment[] | undefined;
  fields: Gql.FieldFieldsFragment[] | undefined;
}) => {
  const sortedSections = sortField(
    sections?.map((s) => s.values).filter((s) => !s.deleted) || [],
    {
      dir: "asc",
      key: "order",
    },
  );
  const sortedFields = sortedSections.flatMap((s) =>
    sortField(
      fields
        ?.filter(
          (f) =>
            f.values.sectionUri === s.uri &&
            f.values.type !== "info" &&
            !f.values.deleted,
        )
        .map((f) => f.values) || [],
      { dir: "asc", key: "order" },
    ),
  );

  const formatPaymentResponse = (value: PaymentResponse | undefined) => {
    if (!value?.chargeRequest) return specialChars.enDash;

    const chargeRequestDescription = value.chargeRequest.description;
    const charges = entriesOf(value?.charges || {});
    if (!charges.length)
      return `Payment Pending${chargeRequestDescription ? ` - ${chargeRequestDescription}` : ""}`;

    return charges
      .map(([, charge]) => {
        let status = "Payment Successful";
        let message = chargeRequestDescription;

        if (charge.status === "FAILED") {
          status = "Payment Failed";
          message =
            charge.smokeballResponse?.failure?.message ||
            "An unexpected error occurred during transaction processing";
        }

        return `${status}${message ? ` - ${message}` : ""}`;
      })
      .join(", ");
  };

  const formatValueForCSV = (
    value: any,
    type: FieldType,
    options?: OptionType[] | null,
  ) => {
    if (type === "payment") return formatPaymentResponse(value);
    if (type === "signature")
      return value?.text || "Signature" || specialChars.enDash;
    return formatValue(value, type, options);
  };

  const rows = [
    [escapeForCSV(formTitle || "")],
    [`Timestamp`, ...sortedFields.map((f) => escapeForCSV(f.label))],
    ...(submissions || []).map((s) => [
      format(s.values.updatedAt, "yyyy-MM-dd HH:mm:ss"),
      ...sortedFields.map((f) => {
        const response = s.responses.find((r) => r.fieldUri === f.uri);
        const parsed = response?.value ? JSON.parse(response.value) : undefined;

        return escapeForCSV(formatValueForCSV(parsed, f.type, f.options));
      }),
    ]),
  ];

  return rows.map((row) => row.join(",")).join("\n");
};
